<template>
  <button
    type="button"
    :class="rootClass"
    :disabled="loading"
    :title="resolvedTitle"
    :aria-label="resolvedTitle"
    @click="handleClick"
  >
    <i v-if="loading" class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
    <i v-else :class="iconClass" aria-hidden="true"></i>
    <span v-if="showLabel">{{ buttonLabel }}</span>
  </button>
</template>

<script>
import axios from 'axios';
import qs from 'qs';
import swal from 'sweetalert2';

export default {
  name: 'message-shortcut-button',
  props: {
    // Target profile info (wrestler or parent)
    profileId: {
      type: Number,
      required: true,
    },
    profileType: {
      type: String,
      required: true,
      validator: (value) => ['WrestlerProfile', 'ParentProfile', 'CoachProfile'].includes(value),
    },
    profileName: {
      type: String,
      required: true,
    },
    // Current coach profile info
    currentProfileId: {
      type: Number,
      required: true,
    },
    currentProfileType: {
      type: String,
      required: true,
    },
    // Guardian info for wrestlers
    guardians: {
      type: Array,
      default: () => [],
    },
    // UI customization
    showLabel: {
      type: Boolean,
      default: true,
    },
    buttonLabel: {
      type: String,
      default: 'Message',
    },
    // When provided, fully replaces the default button styling. Lets the
    // component fit into compact icon rows (e.g. the prospect row) without
    // forcing every caller to inherit the wide primary-button look that
    // suits the parent profile sidebar.
    buttonClass: {
      type: String,
      default: '',
    },
    iconClass: {
      type: String,
      default: 'fa-solid fa-envelope',
    },
    // Title attribute for tooltip — useful in icon-only mode where the
    // label is hidden.
    buttonTitle: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    hasGuardians() {
      return this.guardians && this.guardians.length > 0;
    },
    isWrestler() {
      return this.profileType === 'WrestlerProfile';
    },
    rootClass() {
      return this.buttonClass
        || 'btn btn-primary m-t-sm m-b-xs w-full flex gap-x-xs align-items-center justify-center';
    },
    resolvedTitle() {
      return this.buttonTitle || this.buttonLabel;
    },
  },
  methods: {
    handleClick() {
      if (this.isWrestler && this.hasGuardians) {
        this.showMessageOptions();
      } else {
        // Direct message to the profile (no popup needed)
        this.createDirectMessage([this.buildProfileObject()]);
      }
    },

    buildProfileObject(id, type) {
      return {
        id: id || this.profileId,
        type: type || this.profileType,
      };
    },

    buildCurrentProfileObject() {
      return {
        id: this.currentProfileId,
        type: this.currentProfileType,
      };
    },

    showMessageOptions() {
      const vm = this;
      const firstName = this.profileName.split(' ')[0];

      const optionsHtml = `
        <p class="text-gray-500 m-b-sm">Who would you like to include in the group?</p>
        <div class="swal-message-options">
          <button type="button" class="swal-message-option" data-option="wrestler">
            <span class="option-icon"><i class="fa-solid fa-user"></i></span>
            <span class="option-text">${firstName}</span>
          </button>
          <button type="button" class="swal-message-option" data-option="wrestler-parents">
            <span class="option-icon"><i class="fa-solid fa-users"></i></span>
            <span class="option-text">${firstName} and parents</span>
          </button>
          <button type="button" class="swal-message-option" data-option="parents-only">
            <span class="option-icon"><i class="fa-solid fa-circle-user"></i></span>
            <span class="option-text">Only ${firstName}'s parents</span>
          </button>
        </div>
      `;

      swal.fire({
        title: 'Message',
        html: optionsHtml,
        showConfirmButton: false,
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        buttonsStyling: false,
        customClass: {
          popup: 'swal-message-shortcut',
          cancelButton: 'btn btn-default',
        },
        didOpen: () => {
          const popup = swal.getPopup();
          const options = popup.querySelectorAll('.swal-message-option');

          options.forEach((option) => {
            option.addEventListener('click', () => {
              const selectedOption = option.getAttribute('data-option');
              swal.close();
              vm.handleOptionSelected(selectedOption);
            });
          });
        },
      });
    },

    handleOptionSelected(option) {
      let profiles = [];

      switch (option) {
        case 'wrestler':
          // Just the wrestler
          profiles = [this.buildProfileObject()];
          break;
        case 'wrestler-parents':
          // Wrestler + all guardians
          profiles = [this.buildProfileObject()];
          this.guardians.forEach((guardian) => {
            profiles.push({
              id: guardian.id,
              type: guardian.type,
            });
          });
          break;
        case 'parents-only':
          // Only guardians
          this.guardians.forEach((guardian) => {
            profiles.push({
              id: guardian.id,
              type: guardian.type,
            });
          });
          break;
        default:
          return;
      }

      this.createDirectMessage(profiles);
    },

    async createDirectMessage(targetProfiles) {
      const vm = this;
      vm.loading = true;

      try {
        // Build the full list of profiles (current coach + target profiles)
        const allProfiles = [this.buildCurrentProfileObject(), ...targetProfiles];

        // First, check if a direct message group already exists
        const existingGroup = await vm.findExistingDirectGroup(allProfiles);

        if (existingGroup) {
          // Redirect to existing group
          window.location.href = `/message_groups/${existingGroup.id}`;
          return;
        }

        // Create a new direct message group
        const newGroup = await vm.createNewDirectGroup(allProfiles);

        if (newGroup) {
          // Redirect to the new group
          window.location.href = `/message_groups/${newGroup.id}`;
        }
      } catch (error) {
        vm.loading = false;
        const errorMessage = vm.parseErrorResponse(error);
        vm.$notificationManager.emit('show-toast', errorMessage, false);
      }
    },

    async findExistingDirectGroup(profiles) {
      // Build query params for finding existing direct group
      const params = {
        type: 'direct',
        profiles,
      };

      try {
        const response = await axios.get(`/api/v1/message_groups?${qs.stringify(params, { arrayFormat: 'brackets' })}`);
        const groups = response.data.message_groups;

        if (groups && groups.length > 0) {
          return groups[0];
        }
        return null;
      } catch (error) {
        // If the query fails, proceed to create a new group
        return null;
      }
    },

    async createNewDirectGroup(profiles) {
      const memberships = profiles.map((profile) => ({
        profile_id: profile.id,
        profile_type: profile.type,
        can_publish: true,
      }));

      const payload = {
        message_group: {
          name: '',
          group_type: 'direct',
          default_allow_publish: 'all',
          purpose: '',
          message_memberships_attributes: memberships,
        },
      };

      const response = await axios.post('/api/v1/message_groups', payload);
      return response.data;
    },

    parseErrorResponse(error) {
      if (error.response && error.response.data) {
        const { data } = error.response;
        if (data.errors) {
          if (Array.isArray(data.errors)) {
            return data.errors.join(', ');
          }
          if (typeof data.errors === 'object') {
            return Object.values(data.errors).flat().join(', ');
          }
          return data.errors;
        }
        if (data.error) {
          return data.error;
        }
      }
      return 'An error occurred while creating the message group.';
    },
  },
};
</script>
